<script lang="ts">
import {defineComponent, shallowRef} from 'vue';
import {loadStripe} from '@stripe/stripe-js/pure';
import type {Stripe, StripeElements} from '@stripe/stripe-js';
import {clampMin} from '../js_utils.ts';
import type {Container} from '../container.ts';
import type {AllSpiceCoupon, BillingFrequency} from '../types.ts';

export type AllSpiceBillingFormData = {
  ownerUsernameModel: string,
  ownerEmailModel: string,
  orgNameModel: string,
  committerCountModel: number,
  separateBillingEmailModel: boolean,
  billingEmailModel: string,
  subscriptionFrequencyModel: BillingFrequency,
  checkingPromoCode: boolean,
  promoCodeModel: string,
  promoCodeValid: boolean,
  promoCodeInvalid: boolean,
  promoCodePercent: number,
  promoCodeAmount: number,
  promoCodeMessage: string,
  promoCodeDuration: number,
  promoCodeFrequencies: BillingFrequency[],
  elementsOpts: Record<string, unknown>,
  submitted: boolean,
  isStripeLoaded: boolean,
  discountedPrice: number,
  undiscountedPrice: number,
  undiscountedUnitPrice: number,
  ccSubmissionError: boolean,
  ccSubmissionErrorMsg: string,
  committerDefn: string,
  collaboratorDefn: string,
  committerMonthlyPrice: number,
};

export default defineComponent({
  name: 'AllSpiceBillingForm',
  inject: {
    untypedContainer: {from: 'container'},
  },
  props: {
    clientSecret: {
      type: String,
      required: true,
      default: '',
    },
    publishableKey: {
      type: String,
      required: true,
      default: '',
    },
    monthlyPrice: {
      type: Number,
      required: true,
    },
    yearlyPrice: {
      type: Number,
      required: true,
    },
    ownerUsername: {
      type: String,
      required: false,
      default: '',
    },
    ownerEmail: {
      type: String,
      required: false,
      default: '',
    },
    orgName: {
      type: String,
      required: false,
      default: '',
    },
    committerCount: {
      type: Number,
      required: false,
      default: 1,
    },
    subscriptionFrequency: {
      type: String,
      required: false,
      default: 'monthly',
    },
    separateBillingEmail: {
      type: Boolean,
      required: false,
      default: false,
    },
    billingEmail: {
      type: String,
      required: false,
      default: '',
    },
    committerEntitlementMax: {
      type: Number,
      required: false,
      default: 25,
    },
  },
  setup() {
    const stripe = shallowRef<Stripe | null>(null);
    const elements = shallowRef<StripeElements | null>(null);
    return {stripe, elements};
  },
  data(): AllSpiceBillingFormData {
    return {
      ownerUsernameModel: '',
      ownerEmailModel: '',
      orgNameModel: '',
      committerCountModel: 1,
      separateBillingEmailModel: false,
      billingEmailModel: '',
      subscriptionFrequencyModel: 'monthly',
      checkingPromoCode: false,
      promoCodeModel: '',
      promoCodeValid: false,
      promoCodeInvalid: false,
      promoCodePercent: 0,
      promoCodeAmount: 0,
      promoCodeMessage: '',
      promoCodeDuration: 0,
      promoCodeFrequencies: [],
      elementsOpts: {},
      submitted: false,
      isStripeLoaded: false,
      discountedPrice: 0,
      undiscountedPrice: 0,
      undiscountedUnitPrice: 0,
      ccSubmissionError: false,
      ccSubmissionErrorMsg: '',
      committerDefn: 'Users with push and pull repository access, meaning that they add and modify design files. Typically, electrical and hardware engineers and PCB designers.\n',
      collaboratorDefn: 'Users with read, comment, review, access. Typically firmware, mechanical engineers, managers, vendors, and other stakeholders.\n',
      committerMonthlyPrice: 0,
    };
  },

  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
  },

  mounted() {
    // copy props to data to avoid mutation
    this.ownerUsernameModel = this.ownerUsername;
    this.ownerEmailModel = this.ownerEmail;
    this.orgNameModel = this.orgName;
    this.committerCountModel = this.committerCount;
    this.separateBillingEmailModel = this.separateBillingEmail;
    this.billingEmailModel = this.billingEmail;
    this.subscriptionFrequencyModel = this.subscriptionFrequency as BillingFrequency;

    this.committerMonthlyPrice = this.monthlyPrice;
    this.calculatePrices();

    const appearance = {
      theme: 'stripe',
      variables: {
        colorText: getComputedStyle(document.documentElement)
          .getPropertyValue('--color-text'),
        colorBackground: getComputedStyle(document.documentElement)
          .getPropertyValue('--color-input-background'),
      },
    };

    // eslint-disable-next-line github/no-then
    loadStripe(this.publishableKey).then((stripe: Stripe) => {
      this.stripe = stripe;
      this.elements = this.stripe.elements({clientSecret: this.clientSecret, ...appearance});
      const payment = this.elements.create('payment');
      payment.mount(this.$refs.element as HTMLElement);
      this.isStripeLoaded = true;
    });
  },

  methods: {

    /**
     * Expand or hide a details section
     * @param {HTMLDivElement} elem
     */
    expandDetails(elem: HTMLDivElement) {
      if (elem.style.maxHeight) {
        elem.style.maxHeight = '';
      } else {
        elem.style.maxHeight = `${elem.scrollHeight}px`;
      }
    },

    /**
     * Format a price in USD cents to dollars w/ two decimal places
     * @param {number} price
     * @returns {string}
     */
    formatPrice(price: number) {
      return (price / 100).toFixed(2);
    },

    /**
     * Return the current billing frequency
     * @returns {string}
     */
    formattedBillingFrequency() {
      return (this.subscriptionFrequencyModel === 'monthly') ? 'Monthly' : 'Yearly';
    },

    /**
     * Return the current billing frequency unit
     * @returns {string}
     */
    formattedBillingUnit() {
      return (this.subscriptionFrequencyModel === 'monthly') ? 'month' : 'year';
    },

    /**
     * Whether the promo code applies to the given product
     * @returns {boolean}
     */
    promoCodeApplies() {
      return this.promoCodeValid && this.promoCodeFrequencies.includes(this.subscriptionFrequencyModel);
    },

    /**
     * Calculate the discount based on the current promo code entered
     * @returns {number}
     */
    calculatePromoCodeDiscount() {
      return (this.promoCodeApplies() ?
        (this.promoCodePercent > 0 ?
          (this.undiscountedPrice * (this.promoCodePercent / 100)) :
          (Math.max(this.promoCodeAmount, 0))) : 0);
    },

    /**
     * Calculate the current total price and the current monthly price
     */
    calculatePrices() {
      this.undiscountedUnitPrice = this.subscriptionFrequencyModel === 'monthly' ? this.monthlyPrice : this.yearlyPrice;
      this.undiscountedPrice = this.committerCountModel * this.undiscountedUnitPrice;
      this.discountedPrice = clampMin(this.undiscountedPrice - this.calculatePromoCodeDiscount(), 0);
    },

    /**
     * Validate the form
     * @returns {boolean}
     */
    validForm() {
      return (this.$refs.subForm as HTMLFormElement).reportValidity();
    },

    /**
     * Clear the data related to promo codes
     */
    clearPromoCodeData() {
      this.promoCodePercent = 0;
      this.promoCodeAmount = 0;
      this.promoCodeMessage = '';
      this.promoCodeDuration = 0;
      this.promoCodeFrequencies = [];
    },

    /**
     * Check the validity of a promo code
     * @param {Event} event
     */
    checkPromoCode(event: Event) {
      event.preventDefault();
      // reset state
      this.checkingPromoCode = true;
      this.promoCodeValid = false;
      this.promoCodeInvalid = false;
      this.clearPromoCodeData();

      return this.container.lookup('fetcher').fetch(`/register/promo/${encodeURIComponent(this.promoCodeModel)}`, {
        method: 'GET',
        // eslint-disable-next-line github/no-then
      }).then((response: Response) => response.json()).then((data: AllSpiceCoupon) => {
        this.checkingPromoCode = false;
        if (data !== null) {
          // validate the response
          this.promoCodeValid = data.Valid;
          this.promoCodeInvalid = !data.Valid;
          this.promoCodePercent = data.PercentOff;
          this.promoCodeAmount = data.AmountOff;
          this.promoCodeMessage = data.Message;
          this.promoCodeDuration = data.Duration;
          this.promoCodeFrequencies = data.BillingFrequencies;
          this.calculatePrices();
        } else {
          this.promoCodeInvalid = true;
          this.promoCodeModel = '';
          this.clearPromoCodeData();
        }
      // eslint-disable-next-line github/no-then
      }).catch(() => {
        this.checkingPromoCode = false;
        this.promoCodeInvalid = true;
        this.promoCodeModel = '';
        this.clearPromoCodeData();
      });
    },

    /**
     *
     * @param {Event} event
     */
    async submit(event: Event) {
      event.preventDefault();

      if (!this.isStripeLoaded) return;
      if (!this.validForm()) {
        return;
      }

      // prevent multiple submissions
      if (this.submitted) return;
      this.submitted = true;
      this.ccSubmissionError = false;

      let response = null;

      try {
        const elements = this.elements;
        response = await this.stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/register/status`,
            payment_method_data: {
              metadata: {
                'ownerUsername': this.ownerUsernameModel,
                'ownerEmail': this.ownerEmailModel,
                'orgName': this.orgNameModel,
                'committerCount': this.committerCountModel,
                'separateBillingEmail': this.separateBillingEmailModel,
                'billingEmail': this.billingEmailModel,
                'subscriptionFrequency': this.subscriptionFrequencyModel,
                'promoCode': this.promoCodeApplies() ? this.promoCodeModel : '',
              },
            } as any,
          },
        });
      } finally {
        if (response !== null && response.error) {
          this.ccSubmissionError = true;
          this.ccSubmissionErrorMsg = response.error.message;
          this.submitted = false;
        }
      }
    },
  },
});
</script>

<template>
  <div class="form-content">
    <div
      v-if="submitted"
      class="tw:flex tw:m-2"
    >
      <div class="is-loading tw:w-10" />
      <span class="tw:my-auto">Loading...</span>
    </div>
    <form
      ref="subForm"
      class="ui form payment"
    >
      <div
        class="form-column"
        :style="{filter:submitted ? 'opacity(25%)' : 'opacity(100%)'}"
      >
        <h4 class="ui top attached header">
          Subscribe to AllSpice Hub
        </h4>
        <div class="ui attached segment">
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M12.5 7.25a.75.75 0 00-1.5 0v5.5c0 .27.144.518.378.651l3.5 2a.75.75 0 00.744-1.302L12.5 12.315V7.25z" />
                <path
                  fill-rule="evenodd"
                  d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zM2.5 12a9.5 9.5 0 1119 0 9.5 9.5 0 01-19 0z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>How often do you want to be billed?</span>
              </div>
              <div class="section-body">
                <div class="frequency-toggle">
                  <div class="field frequency">
                    <div class="ui radio">
                      <input
                        id="yearly"
                        v-model="subscriptionFrequencyModel"
                        type="radio"
                        value="yearly"
                        :disabled="submitted"
                        @change="calculatePrices"
                      >
                      <label
                        style="cursor: pointer"
                        for="yearly"
                      >
                        Yearly
                        <span
                          class="emphasize"
                          style="white-space: nowrap;"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            width="16"
                            height="16"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M2.5 7.775V2.75a.25.25 0 01.25-.25h5.025a.25.25 0 01.177.073l6.25 6.25a.25.25 0 010 .354l-5.025 5.025a.25.25 0 01-.354 0l-6.25-6.25a.25.25 0 01-.073-.177zm-1.5 0V2.75C1 1.784 1.784 1 2.75 1h5.025c.464 0 .91.184 1.238.513l6.25 6.25a1.75 1.75 0 010 2.474l-5.026 5.026a1.75 1.75 0 01-2.474 0l-6.25-6.25A1.75 1.75 0 011 7.775zM6 5a1 1 0 100 2 1 1 0 000-2z"
                            />
                          </svg>
                          10% off
                        </span>
                      </label>
                    </div>
                  </div>
                  <div class="field frequency">
                    <div class="ui radio">
                      <input
                        id="monthly"
                        v-model="subscriptionFrequencyModel"
                        type="radio"
                        value="monthly"
                        :disabled="submitted"
                        @change="calculatePrices"
                      >
                      <label
                        style="cursor: pointer"
                        for="monthly"
                      >
                        Monthly
                      </label>
                    </div>
                  </div>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M3.5 8a5.5 5.5 0 118.596 4.547 9.005 9.005 0 015.9 8.18.75.75 0 01-1.5.045 7.5 7.5 0 00-14.993 0 .75.75 0 01-1.499-.044 9.005 9.005 0 015.9-8.181A5.494 5.494 0 013.5 8zM9 4a4 4 0 100 8 4 4 0 000-8z"
                />
                <path d="M17.29 8c-.148 0-.292.01-.434.03a.75.75 0 11-.212-1.484 4.53 4.53 0 013.38 8.097 6.69 6.69 0 013.956 6.107.75.75 0 01-1.5 0 5.193 5.193 0 00-3.696-4.972l-.534-.16v-1.676l.41-.209A3.03 3.03 0 0017.29 8z" />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>How many <label
                  class="allspice-elaborate"
                  :title="committerDefn"
                >committer</label> seats do you need to start?</span>
              </div>
              <div class="section-body">
                <span class="desc larger">All plans include <span class="emphasize">unlimited</span> free <label
                  class="allspice-elaborate"
                  :title="collaboratorDefn"
                >collaborators</label></span>
                <div class="field">
                  <input
                    id="premiumSeatCount"
                    v-model="committerCountModel"
                    pattern="[0-9]*"
                    type="number"
                    min="1"
                    oninput="validity.valid||(value=1);"
                    :max="committerEntitlementMax"
                    :disabled="submitted"
                    required
                    @change="calculatePrices"
                  >
                  <span class="desc">The number of <label
                    class="allspice-elaborate"
                    :title="committerDefn"
                  >committers</label> can be changed at any time in Organization settings.</span>
                </div>
                <a @click="expandDetails($refs.priceDetails as HTMLDivElement)">Details</a>
                <div
                  ref="priceDetails"
                  class="collapsed-details"
                >
                  <div
                    class="definition"
                    style="padding-top: 15px"
                  >
                    <label class="allspice-elaborate">Committers</label><br>
                    {{ committerDefn }}
                  </div>
                  <div class="definition">
                    <label class="allspice-elaborate">Collaborators</label><br>
                    {{ collaboratorDefn }}
                  </div>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M6.75 0a.75.75 0 01.75.75V3h9V.75a.75.75 0 011.5 0V3h2.75c.966 0 1.75.784 1.75 1.75v16a1.75 1.75 0 01-1.75 1.75H3.25a1.75 1.75 0 01-1.75-1.75v-16C1.5 3.784 2.284 3 3.25 3H6V.75A.75.75 0 016.75 0zm-3.5 4.5a.25.25 0 00-.25.25V8h18V4.75a.25.25 0 00-.25-.25H3.25zM21 9.5H3v11.25c0 .138.112.25.25.25h17.5a.25.25 0 00.25-.25V9.5z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>{{ formattedBillingFrequency() }} Price</span>
                <div
                  class="section-price"
                  data-testid="price"
                >
                  {{ committerCountModel }} x &#36;{{ formatPrice(undiscountedUnitPrice) }}
                </div>
              </div>
              <div class="section-body">
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M20.322.75a10.75 10.75 0 00-7.373 2.926l-1.304 1.23A23.743 23.743 0 0010.103 6.5H5.066a1.75 1.75 0 00-1.5.85l-2.71 4.514a.75.75 0 00.49 1.12l4.571.963c.039.049.082.096.129.14L8.04 15.96l1.872 1.994c.044.047.091.09.14.129l.963 4.572a.75.75 0 001.12.488l4.514-2.709a1.75 1.75 0 00.85-1.5v-5.038a23.741 23.741 0 001.596-1.542l1.228-1.304a10.75 10.75 0 002.925-7.374V2.499A1.75 1.75 0 0021.498.75h-1.177zM16 15.112c-.333.248-.672.487-1.018.718l-3.393 2.262.678 3.223 3.612-2.167a.25.25 0 00.121-.214v-3.822zm-10.092-2.7L8.17 9.017c.23-.346.47-.685.717-1.017H5.066a.25.25 0 00-.214.121l-2.167 3.612 3.223.679zm8.07-7.644a9.25 9.25 0 016.344-2.518h1.177a.25.25 0 01.25.25v1.176a9.25 9.25 0 01-2.517 6.346l-1.228 1.303a22.248 22.248 0 01-3.854 3.257l-3.288 2.192-1.743-1.858a.764.764 0 00-.034-.034l-1.859-1.744 2.193-3.29a22.248 22.248 0 013.255-3.851l1.304-1.23zM17.5 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-11 13c.9-.9.9-2.6 0-3.5-.9-.9-2.6-.9-3.5 0-1.209 1.209-1.445 3.901-1.49 4.743a.232.232 0 00.247.247c.842-.045 3.534-.281 4.743-1.49z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Do you have a promo code?</span>
              </div>
              <div class="section-body">
                <div class="inline field">
                  <div class="promo-entry tw:mr-0">
                    <input
                      id="promoCode"
                      v-model="promoCodeModel"
                      type="text"
                      autofocus
                      :disabled="checkingPromoCode || submitted || promoCodeValid"
                    >
                    <button
                      id="promoCodeButton"
                      ref="promoCodeButton"
                      :disabled="checkingPromoCode || submitted || promoCodeModel === '' || promoCodeValid"
                      class="ui blue inline button"
                      style="margin-left: 10px"
                      @click="checkPromoCode"
                    >
                      Apply
                    </button>
                    <span class="promo-status-group">
                      <svg
                        v-if="promoCodeValid"
                        class="success-color"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="28"
                        height="28"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M21.03 5.72a.75.75 0 010 1.06l-11.5 11.5a.75.75 0 01-1.072-.012l-5.5-5.75a.75.75 0 111.084-1.036l4.97 5.195L19.97 5.72a.75.75 0 011.06 0z"
                        />
                      </svg>
                      <svg
                        v-if="promoCodeInvalid"
                        class="failure-color"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="28"
                        height="28"
                      >
                        <path d="M5.72 5.72a.75.75 0 0 1 1.06 0L12 10.94l5.22-5.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L13.06 12l5.22 5.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L12 13.06l-5.22 5.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L10.94 12 5.72 6.78a.75.75 0 0 1 0-1.06Z" />
                      </svg>
                      <span
                        v-if="checkingPromoCode"
                        class="is-loading tw:block tw:size-[28px]"
                      />
                    </span>
                  </div>
                  <div
                    v-if="promoCodeMessage !== ''"
                    class="desc price-desc"
                    style="padding-top: 10px"
                  >
                    {{ promoCodeMessage }}
                  </div>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div
            v-if="promoCodeApplies()"
            class="section"
          >
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M20.322.75a10.75 10.75 0 00-7.373 2.926l-1.304 1.23A23.743 23.743 0 0010.103 6.5H5.066a1.75 1.75 0 00-1.5.85l-2.71 4.514a.75.75 0 00.49 1.12l4.571.963c.039.049.082.096.129.14L8.04 15.96l1.872 1.994c.044.047.091.09.14.129l.963 4.572a.75.75 0 001.12.488l4.514-2.709a1.75 1.75 0 00.85-1.5v-5.038a23.741 23.741 0 001.596-1.542l1.228-1.304a10.75 10.75 0 002.925-7.374V2.499A1.75 1.75 0 0021.498.75h-1.177zM16 15.112c-.333.248-.672.487-1.018.718l-3.393 2.262.678 3.223 3.612-2.167a.25.25 0 00.121-.214v-3.822zm-10.092-2.7L8.17 9.017c.23-.346.47-.685.717-1.017H5.066a.25.25 0 00-.214.121l-2.167 3.612 3.223.679zm8.07-7.644a9.25 9.25 0 016.344-2.518h1.177a.25.25 0 01.25.25v1.176a9.25 9.25 0 01-2.517 6.346l-1.228 1.303a22.248 22.248 0 01-3.854 3.257l-3.288 2.192-1.743-1.858a.764.764 0 00-.034-.034l-1.859-1.744 2.193-3.29a22.248 22.248 0 013.255-3.851l1.304-1.23zM17.5 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-11 13c.9-.9.9-2.6 0-3.5-.9-.9-2.6-.9-3.5 0-1.209 1.209-1.445 3.901-1.49 4.743a.232.232 0 00.247.247c.842-.045 3.534-.281 4.743-1.49z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Discount</span>
                <div
                  class="section-price emphasize"
                  data-testid="discountPrice"
                >
                  -&#36;{{ formatPrice(undiscountedPrice - discountedPrice) }}
                </div>
              </div>
              <div class="section-body">
                <div
                  v-if="promoCodeDuration >= 1"
                  class="desc price-desc"
                >
                  Your first {{ promoCodeDuration === 1 ? `${formattedBillingUnit()} is` : `${promoCodeDuration} ${formattedBillingUnit()}s are` }}
                  <span class="emphasize">
                    <template v-if="promoCodePercent > 0">
                      {{ promoCodePercent }}% off!
                    </template>
                    <template v-else-if="promoCodeAmount > 0">
                      ${{ formatPrice(promoCodeAmount) }} off!
                    </template>
                  </span>
                </div>
                <div
                  v-if="promoCodeDuration === -1"
                  class="desc price-desc"
                >
                  Your {{ formattedBillingUnit() }}ly subscription is perpetually
                  <span class="emphasize">
                    <template v-if="promoCodePercent > 0">
                      {{ promoCodePercent }}% off!
                    </template>
                    <template v-else-if="promoCodeAmount > 0">
                      ${{ formatPrice(promoCodeAmount) }} off!
                    </template>
                  </span>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div
            v-else-if="promoCodeValid"
            class="section"
          >
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M20.322.75a10.75 10.75 0 00-7.373 2.926l-1.304 1.23A23.743 23.743 0 0010.103 6.5H5.066a1.75 1.75 0 00-1.5.85l-2.71 4.514a.75.75 0 00.49 1.12l4.571.963c.039.049.082.096.129.14L8.04 15.96l1.872 1.994c.044.047.091.09.14.129l.963 4.572a.75.75 0 001.12.488l4.514-2.709a1.75 1.75 0 00.85-1.5v-5.038a23.741 23.741 0 001.596-1.542l1.228-1.304a10.75 10.75 0 002.925-7.374V2.499A1.75 1.75 0 0021.498.75h-1.177zM16 15.112c-.333.248-.672.487-1.018.718l-3.393 2.262.678 3.223 3.612-2.167a.25.25 0 00.121-.214v-3.822zm-10.092-2.7L8.17 9.017c.23-.346.47-.685.717-1.017H5.066a.25.25 0 00-.214.121l-2.167 3.612 3.223.679zm8.07-7.644a9.25 9.25 0 016.344-2.518h1.177a.25.25 0 01.25.25v1.176a9.25 9.25 0 01-2.517 6.346l-1.228 1.303a22.248 22.248 0 01-3.854 3.257l-3.288 2.192-1.743-1.858a.764.764 0 00-.034-.034l-1.859-1.744 2.193-3.29a22.248 22.248 0 013.255-3.851l1.304-1.23zM17.5 8a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm-11 13c.9-.9.9-2.6 0-3.5-.9-.9-2.6-.9-3.5 0-1.209 1.209-1.445 3.901-1.49 4.743a.232.232 0 00.247.247c.842-.045 3.534-.281 4.743-1.49z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span data-testid="promoWrongFrequency">The promo code you entered doesn't apply to this billing frequency.</span>
              </div>
              <div class="section-body">
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M15.25 14a.75.75 0 000 1.5h3.5a.75.75 0 000-1.5h-3.5z" />
                <path
                  fill-rule="evenodd"
                  d="M1.75 3A1.75 1.75 0 000 4.75v14.5C0 20.216.784 21 1.75 21h20.5A1.75 1.75 0 0024 19.25V4.75A1.75 1.75 0 0022.25 3H1.75zM1.5 4.75a.25.25 0 01.25-.25h20.5a.25.25 0 01.25.25V8.5h-21V4.75zm0 5.25v9.25c0 .138.112.25.25.25h20.5a.25.25 0 00.25-.25V10h-21z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Due today</span>
                <div
                  class="section-price"
                  data-testid="totalDue"
                >
                  &#36;{{ formatPrice(discountedPrice) }}
                </div>
              </div>
              <div class="section-body">
                <div
                  v-if="promoCodeDuration > 0"
                  class="desc price-desc"
                >
                  <!--Limited discount-->
                  Then &#36;{{ formatPrice(undiscountedPrice) }}/{{ formattedBillingUnit() }} starting after your first discounted {{ promoCodeDuration === 1 ? formattedBillingUnit() : `${promoCodeDuration} ${formattedBillingUnit()}s` }}.
                </div>
                <div
                  v-else-if="promoCodeDuration === 0"
                  class="desc price-desc"
                >
                  <!--No discount-->
                  Then &#36;{{ formatPrice(undiscountedPrice) }}/{{ formattedBillingUnit() }}.
                </div>
                <div
                  v-else
                  class="desc price-desc"
                >
                  <!--Perpetual discount-->
                  Then &#36;{{ formatPrice(discountedPrice) }}/{{ formattedBillingUnit() }}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="form-column"
        :style="{filter:submitted ? 'opacity(25%)' : 'opacity(100%)'}"
      >
        <div class="ui attached segment">
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  fill-rule="evenodd"
                  d="M0 4.75C0 3.784.784 3 1.75 3h20.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0122.25 21H1.75A1.75 1.75 0 010 19.25V4.75zm1.75-.25a.25.25 0 00-.25.25v14.5c0 .138.112.25.25.25h20.5a.25.25 0 00.25-.25V4.75a.25.25 0 00-.25-.25H1.75z"
                />
                <path
                  fill-rule="evenodd"
                  d="M5 8.75A.75.75 0 015.75 8h11.5a.75.75 0 010 1.5H5.75A.75.75 0 015 8.75zm0 4a.75.75 0 01.75-.75h5.5a.75.75 0 010 1.5h-5.5a.75.75 0 01-.75-.75z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Account Information</span>
              </div>
              <div class="section-body">
                <div class="required field">
                  <label for="ownerUsername">Organization Owner Username</label>
                  <input
                    id="ownerUsername"
                    v-model="ownerUsernameModel"
                    autofocus
                    required
                    pattern="^[\w.\-_]+$"
                    :disabled="submitted"
                  >
                  <span class="help">Organization owner username must contain only alphanumeric characters, '.', '_', or '-'</span>
                </div>
                <div class="required field">
                  <label for="ownerEmail">Organization Owner Email</label>
                  <input
                    id="ownerEmail"
                    v-model="ownerEmailModel"
                    type="email"
                    autofocus
                    required
                    :disabled="submitted"
                  >
                </div>
                <div class="required field">
                  <label for="orgName">Organization Name</label>
                  <input
                    id="orgName"
                    v-model="orgNameModel"
                    autofocus
                    required
                    pattern="^[\w.\-_]+$"
                    :disabled="submitted"
                  >
                  <span class="help">Organization name must contain only alphanumeric characters, '.', '_', or '-'</span>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M6.25 12a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zM5.5 9.25a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5a.75.75 0 01-.75-.75zM6.25 5a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zM9 12.75a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5a.75.75 0 01-.75-.75zm.75-4.25a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zM9 5.75A.75.75 0 019.75 5h.5a.75.75 0 010 1.5h-.5A.75.75 0 019 5.75zM13.25 12a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5zm-.75-2.75a.75.75 0 01.75-.75h.5a.75.75 0 010 1.5h-.5a.75.75 0 01-.75-.75zM13.25 5a.75.75 0 000 1.5h.5a.75.75 0 000-1.5h-.5z" />
                <path
                  fill-rule="evenodd"
                  d="M2 20a2 2 0 002 2h3.75a.75.75 0 00.75-.75V19h3v2.25c0 .414.336.75.75.75H16c.092 0 .183-.006.272-.018a.753.753 0 00.166.018H20a2 2 0 002-2v-8a2 2 0 00-.8-1.6l-.5-.375a.75.75 0 00-.9 1.2l.5.375a.5.5 0 01.2.4v8a.5.5 0 01-.5.5h-2.063c.041-.16.063-.327.063-.5V3a2 2 0 00-2-2H4a2 2 0 00-2 2v17zm2 .5a.5.5 0 01-.5-.5V3a.5.5 0 01.5-.5h12a.5.5 0 01.5.5v17a.5.5 0 01-.5.5h-3v-2.25a.75.75 0 00-.75-.75h-4.5a.75.75 0 00-.75.75v2.25H4z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Billing Information</span>
              </div>
              <div class="section-body">
                <div class="grouped field">
                  <div class="inline field">
                    <label
                      style="cursor: pointer"
                      for="separateBillingEmail"
                    >
                      <input
                        id="separateBillingEmail"
                        v-model="separateBillingEmailModel"
                        style="margin: 2px 5px 0 0"
                        type="checkbox"
                        :disabled="submitted"
                      >
                      Use separate email address for billing-related communication
                    </label>
                  </div>
                  <div
                    v-show="separateBillingEmailModel"
                    class="inline field"
                  >
                    <label for="billingEmail">Customer Billing Email</label>
                    <input
                      id="billingEmail"
                      v-model="billingEmailModel"
                      type="email"
                      autofocus
                      :disabled="submitted"
                    >
                  </div>
                  <span class="desc">Tax ID and Company Address can be updated in the billing portal after registration.</span>
                </div>
                <div class="ui divider" />
              </div>
            </div>
          </div>
          <div class="section">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M15.25 14a.75.75 0 000 1.5h3.5a.75.75 0 000-1.5h-3.5z" />
                <path
                  fill-rule="evenodd"
                  d="M1.75 3A1.75 1.75 0 000 4.75v14.5C0 20.216.784 21 1.75 21h20.5A1.75 1.75 0 0024 19.25V4.75A1.75 1.75 0 0022.25 3H1.75zM1.5 4.75a.25.25 0 01.25-.25h20.5a.25.25 0 01.25.25V8.5h-21V4.75zm0 5.25v9.25c0 .138.112.25.25.25h20.5a.25.25 0 00.25-.25V10h-21z"
                />
              </svg>
            </div>
            <div class="section-content">
              <div class="section-header">
                <span>Payment Method</span>
              </div>
              <div class="section-body">
                <div class="field">
                  <div
                    v-if="ccSubmissionError"
                    class="ui negative message flash-error"
                  >
                    <p>{{ ccSubmissionErrorMsg }}</p>
                  </div>
                  <div ref="element">
                    <div class="tw:flex tw:m-2">
                      <div class="is-loading tw:w-10" />
                      <span class="tw:my-auto">Loading payment form details...</span>
                    </div>
                  </div>
                </div>
                <div class="inline field">
                  <label
                    style="cursor: pointer"
                    for="acceptTerms"
                  >
                    <input
                      id="acceptTerms"
                      style="margin: 2px 5px 0 0"
                      type="checkbox"
                      :disabled="submitted"
                      required
                    >
                    Accept <a
                      target="_blank"
                      href="https://www.allspice.io/terms"
                    >terms and conditions</a>
                  </label>
                </div>
                <div class="field">
                  <button
                    ref="submitButton"
                    :disabled="submitted"
                    class="ui primary button"
                    @click="submit"
                  >
                    Subscribe
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
  .form-content {
    height: 100vh;
  }
  .section {
    display: grid;
    grid-template-columns: 34px 1fr;
  }
  .section-icon {
    padding: 5px;
  }
  .section-icon svg {
    fill: var(--color-secondary-dark-6);
    vertical-align: middle;
  }
  .section-content {
    padding: 5px;
  }
  .section-header {
    font-size: 18px;
    padding-bottom: 10px;
  }
  .section-header span {
    padding-bottom: 10px;
    font-weight: var(--font-weight-semibold);
    display: inline;
    line-height: 150%;
  }
  .section-body {
    display: block;
    padding-right: 15px;
  }
  .section-price {
    text-align: right;
    font-size: 24px;
    display: inline;
    position:absolute;
    right: 0;
    padding-right: 35px;
  }
  .emphasize {
    color: var(--color-allspice-accent-green);
  }
  .emphasize svg {
    fill: var(--color-allspice-accent-green);
    margin-left: 10px;
  }
  .success-color {
    fill: var(--color-allspice-accent-green);
  }
  .failure-color {
    fill: red;
  }
  .collapsed-details {
    padding: 0 18px;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease-out;
  }
  .definition {
    padding-bottom: 10px;
  }
  .desc {
    color: var(--color-text-light-2);
    display: block;
  }
  .price-desc {
    text-align: left;
  }
  .larger {
    font-size: 16px;
    padding-bottom: 5px;
  }
  .frequency-toggle {
    display: grid;
    grid-template-rows: 1fr 1fr;
  }
  .form-column {
    float: left;
    width: 50%;
    padding: 0 10px;
  }
  .promo-entry {
    display: grid;
    grid-template-columns: 4fr 2fr 1fr;
  }
  .promo-status-group {
    width: 30px;
    height: 30px;
    margin-left: 10px;
    margin-top: auto;
    margin-bottom: auto;
  }
  @media only screen and (max-width: 990px) {
    .form-column {
      width: 100%;
    }
  }
</style>
